import type { RouterInputs } from '@/utils/api';
import { updateWithToast } from '@/clientOnlyUtils/clientUtils';
import { useState } from 'react';

import { cn } from '@poseidon/components/cn';
import { Button, SingleCheckbox } from '@poseidon/components/Input2';
import { Popup } from '@poseidon/components/Popup';
import { QUALIFY_REJECT_MAX } from '@poseidon/shared-utils/qualifyFit';
import { RESCORE_MAX } from '@poseidon/shared-utils/rescoreLimits';

import type { CampaignPerson } from '~/components/CampaignOwner/campaignOwner';
import { api } from '@/utils/api';
import { campaignOf } from '~/components/CampaignOwner/campaignOwner';
import { CampaignSendersNotice } from '~/components/CampaignOwner/CampaignSendersNotice';

type TakeOutGroup = RouterInputs['cadenceTemplate']['rescoreStart']['takeOut'][number];

/** The three groups a person can tick to take out on a `no`, in the confirm's order. Nothing is ticked for them. */
const TAKE_OUT_ROWS: { key: TakeOutGroup; title: string; what: string }[] = [
  {
    key: 'contacted',
    title: 'Already contacted',
    what: 'Their remaining steps stop and they leave the campaign. Anyone who replied, paused or finished is never taken out.',
  },
  {
    key: 'notsent',
    title: 'Not contacted yet',
    what: 'Nothing was sent. They leave the campaign and are marked as not a fit.',
  },
  {
    key: 'byhand',
    title: 'Added by hand',
    what: 'Added on purpose, so they stay unless you tick this. They are scored either way.',
  },
];

const REFUSALS: Record<'run_active' | 'nothing_to_rescore' | 'no_audience', string> = {
  run_active: 'A re-screen is already running on this campaign. Wait for it to finish, then try again.',
  nothing_to_rescore: 'Nobody selected has a screening record to score.',
  no_audience: 'This campaign has no audience to score against. Describe one on the Audience tab first.',
};

const people = (n: number) => `${n.toLocaleString()} ${n === 1 ? 'person' : 'people'}`;

/**
 * "Re-score N people?" (2026-10-23), from the dots menu on the People table's selection bar. Copies
 * the Re-check confirm: title, whose campaign and whose LinkedIn, Cancel and one primary button.
 * Under the body sit the three take-out rows in the audience re-screen picker's layout, all
 * unticked: re-scoring always runs, and a tick is the only way anyone leaves.
 */
export function RescoreConfirmPopup({
  cadenceTemplateId,
  cadenceIds,
  owner,
  acting,
  onClose,
  onStarted,
}: {
  cadenceTemplateId: string;
  /** The selected enrollments (cadence public ids). */
  cadenceIds: string[];
  owner: CampaignPerson | null;
  acting: CampaignPerson | null;
  onClose: () => void;
  onStarted: () => void;
}) {
  const [picked, setPicked] = useState<TakeOutGroup[]>([]);
  // Over the cap the routes would refuse the ids; say so instead of counting.
  const overCap = cadenceIds.length > RESCORE_MAX;
  const countsQuery = api.cadenceTemplate.rescoreCounts.useQuery(
    { cadenceTemplateId, cadenceIds },
    { enabled: !overCap },
  );
  const startMutation = api.cadenceTemplate.rescoreStart.useMutation();
  const utils = api.useUtils();
  const counts = countsQuery.data;
  const total = counts?.total ?? 0;
  const toggle = (key: TakeOutGroup) => setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  // Only the people a `no` could actually remove: a replied, finished or paused enrollment keeps its place.
  const atRisk = counts == null ? 0 : picked.reduce((sum, key) => sum + counts.canTakeOut[key], 0);
  const summary = overCap
    ? `Re-score up to ${RESCORE_MAX.toLocaleString()} at a time · ${people(cadenceIds.length)} selected`
    : counts == null
      ? countsQuery.isError
        ? 'The count could not be loaded. Close this and try again.'
        : 'Counting…'
      : total === 0
        ? 'Nobody selected has a screening record to score.'
        : atRisk === 0
          ? `${people(total)} ${total === 1 ? 'gets' : 'get'} a new score · nobody is taken out`
          : `${people(total)} ${total === 1 ? 'gets' : 'get'} a new score · up to ${atRisk.toLocaleString()} may be taken out`;

  const onStart = () =>
    updateWithToast({
      loading: 'Starting the re-score…',
      success: `Re-scoring ${people(total)}. Scores update as each one lands.`,
      mutationFnAsync: async () => {
        const res = await startMutation.mutateAsync({ cadenceTemplateId, cadenceIds, takeOut: picked });
        if (!res.ok) throw new Error(REFUSALS[res.reason]);
        void utils.cadenceTemplate.candidateRescreenRun.invalidate({ cadenceTemplateId });
        void utils.cadenceTemplate.peopleList.invalidate({ cadenceTemplateId });
        onStarted();
      },
    });

  return (
    <Popup onClose={onClose} className="max-w-md p-0 text-left" data-testid="rescore-popup">
      <div className="flex flex-col gap-3 p-5">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Re-score {people(total)}
            {owner != null ? ` in ${campaignOf(owner)}` : ''}?
          </h3>
          <p className="mt-1 text-[13px] text-lynch-700 dark:text-lynch-400">
            Each one is scored again by the current judge, using the research already on file. The new score replaces
            the old one. Nobody leaves the campaign unless you tick a group below.
          </p>
        </div>

        <CampaignSendersNotice owner={owner} acting={acting} className="mt-0" />

        {counts != null && counts.noRecord > 0 && (
          <p className="text-xs text-lynch-600 dark:text-lynch-500" data-testid="rescore-no-record">
            {people(counts.noRecord)} selected {counts.noRecord === 1 ? 'has' : 'have'} no screening record and{' '}
            {counts.noRecord === 1 ? 'is' : 'are'} left as is.
          </p>
        )}

        <div className="flex flex-col">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-lynch-700 dark:text-lynch-500">
            Take out anyone the judge now rejects ({QUALIFY_REJECT_MAX} or less out of 10)
          </div>
          {TAKE_OUT_ROWS.map((row) => {
            const n = counts?.groups[row.key] ?? 0;
            const disabled = n === 0;
            const on = picked.includes(row.key);
            return (
              <div
                key={row.key}
                data-testid={`rescore-group-${row.key}`}
                onClick={disabled ? undefined : () => toggle(row.key)}
                className={cn(
                  'grid grid-cols-[auto_3.5rem_minmax(0,1fr)] gap-x-3 rounded-md border-t border-lynch-100 px-1 py-2 dark:border-lynch-800',
                  !disabled && 'cursor-pointer hover:bg-lynch-50 dark:hover:bg-white/5',
                  disabled && 'opacity-50',
                )}>
                <SingleCheckbox
                  size="sm"
                  checked={on}
                  disabled={disabled}
                  aria-label={row.title}
                  className="mt-0.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) toggle(row.key);
                  }}
                />
                <span className="text-right text-[15px] font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                  {n.toLocaleString()}
                </span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-medium text-gray-900 dark:text-gray-100">{row.title}</span>
                  <span className="block text-xs text-lynch-700 dark:text-lynch-400">{row.what}</span>
                </span>
              </div>
            );
          })}
          <p className="mt-2 px-1 text-xs text-lynch-600 dark:text-lynch-500">
            Unticked groups keep their place whatever the new score. Every re-score lands in Decisions with the old
            score beside it.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-lynch-100 pt-3 dark:border-lynch-800">
          <span className="text-[13px] text-lynch-800 dark:text-lynch-300" data-testid="rescore-summary">
            {summary}
          </span>
          <span className="flex gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              color="blue"
              disabled={overCap || counts == null || total === 0 || startMutation.isPending}
              status={startMutation.isPending ? 'updating' : undefined}
              onClick={() => void onStart()}
              data-testid="rescore-start">
              {total === 0 ? 'Re-score' : `Re-score ${total.toLocaleString()}`}
            </Button>
          </span>
        </div>
      </div>
    </Popup>
  );
}
